import { useEffect, useState } from 'react';
import type { ConsumoDeWhatsApp, CosteDeWhatsApp, DetalleDeExtracto, DiferenciaDeCoste, ExtractoDeYCloud, RecargaDelMonedero, ResolucionDeDiferencia } from '../../api/ops/backoffice';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { cifraDinero, simboloMoneda } from '../../logica/dinero';
import { textoDeError } from '../negocios/logica';
import '../../componentes/cifras.css';
import { estadoDeError } from '../../logica/estados';
import type { Mes } from '../../partners/logica';
import { errorDelMarco, todasLasPaginas } from '../partners/carga';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { MESES_CORTOS, MONEDA_MONEDERO, RESOLUCIONES, estadoCierre, filaRecarga, filasCoste, filasConciliacion, hoyEnMadrid, mesFuturo, mesInicial, periodoDe, resolucionesDe, textoDelMes, type FilaConciliacion } from './logica';
import { PanelCoste, PanelImportar, VentanaRecarga, VentanaResolver } from './Ventanas';
import './ycloud.css';

/**
 * **Conciliación YCloud** (1.22, solo Superadmin): el mes (año y tira de meses), las cifras del extracto, sus líneas
 * frente a lo que hemos contado (`getWhatsAppCostStatement`, `listWhatsAppCostDiscrepancies`, `getWhatsAppUsageSummary`)
 * con las cuatro formas de resolver una diferencia (`resolveWhatsAppCostDiscrepancy`), y el coste por mensaje de Meta
 * (`listWhatsAppCostRates`) con «Registrar el coste del trimestre» (`createWhatsAppCostRate`; 4 decimales, G1149).
 *
 * Las cifras del extracto salen de la API (`recorded_total`, `difference_total` y `wallet_balance` en dólares, G1155,
 * G1154): el panel no suma dinero (G290). El nombre de cada negocio llega en las líneas y diferencias (G1140). Una
 * diferencia reclamada a YCloud sigue abierta (`claimed`, G1153). Provisionales hasta que diseño las dibuje: «Cerrar el
 * mes» (`closeWhatsAppCostStatement`, G1152, C.67), «Importar el extracto de YCloud» subiendo el CSV, que se concilia al
 * importar (`importWhatsAppCostStatement`, G1156, C.68), y «Recargas del monedero» (`listYCloudWalletTopUps`,
 * `createYCloudWalletTopUp`, G1157, C.69).
 */
interface DatosMes {
    extracto: DetalleDeExtracto | null;
    diferencias: DiferenciaDeCoste[];
    consumo: ConsumoDeWhatsApp[];
}

export function YCloud(p: PropsSeccion) {
    const { ops, movil, onSesionCaducada, onFaltanDosPasos } = p;
    const avisar = useAvisoBreve();
    const [ahora] = useState(() => new Date());
    const [mes, setMes] = useState<Mes>(() => mesInicial(ahora));
    const [extractos, setExtractos] = useState<ExtractoDeYCloud[] | null>(null);
    const [mesCargado, setMesCargado] = useState<{ periodo: string; datos: DatosMes } | null>(null);
    const [costes, setCostes] = useState<CosteDeWhatsApp[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [vuelta, setVuelta] = useState(0);
    const [vueltaCostes, setVueltaCostes] = useState(0);
    const [resolver, setResolver] = useState<{ fila: FilaConciliacion; resolucion: ResolucionDeDiferencia } | null>(null);
    const [panelCoste, setPanelCoste] = useState(false);
    const [panelImportar, setPanelImportar] = useState(false);
    const [recargas, setRecargas] = useState<RecargaDelMonedero[] | null>(null);
    const [vueltaRecargas, setVueltaRecargas] = useState(0);
    const [ventanaRecarga, setVentanaRecarga] = useState(false);
    const [cerrando, setCerrando] = useState(false);
    const periodo = periodoDe(mes);
    const hoy = hoyEnMadrid(ahora);

    // Los extractos importados (uno por mes): se piden una vez y se busca el del mes elegido.
    useEffect(() => {
        let vivo = true;
        todasLasPaginas((cursor) => ops.listWhatsAppCostStatements({ cursor, limit: 100 }))
            .then((lista) => {
                if (vivo) {
                    setExtractos(lista);
                }
            })
            .catch((e: unknown) => {
                if (vivo && !errorDelMarco(e, { onSesionCaducada, onFaltanDosPasos })) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, vuelta, onSesionCaducada, onFaltanDosPasos]);

    useEffect(() => {
        let vivo = true;
        todasLasPaginas((cursor) => ops.listWhatsAppCostRates({ country: 'ES', cursor, limit: 100 }))
            .then((lista) => {
                if (vivo) {
                    setCostes(lista);
                }
            })
            .catch((e: unknown) => {
                if (vivo && !errorDelMarco(e, { onSesionCaducada, onFaltanDosPasos })) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, vuelta, vueltaCostes, onSesionCaducada, onFaltanDosPasos]);

    // Recargas del monedero (G1157), de la más reciente a la más antigua.
    useEffect(() => {
        let vivo = true;
        todasLasPaginas((cursor) => ops.listYCloudWalletTopUps({ cursor, limit: 100 }))
            .then((lista) => {
                if (vivo) {
                    setRecargas(lista);
                }
            })
            .catch((e: unknown) => {
                if (vivo && !errorDelMarco(e, { onSesionCaducada, onFaltanDosPasos })) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, vuelta, vueltaRecargas, onSesionCaducada, onFaltanDosPasos]);

    useEffect(() => {
        if (extractos === null) {
            return;
        }
        let vivo = true;
        const delMes = extractos.find((x) => x.period === periodo);
        const cargar = async (): Promise<DatosMes> => {
            if (delMes === undefined) {
                return { extracto: null, diferencias: [], consumo: [] };
            }
            const [extracto, diferencias, consumo] = await Promise.all([
                ops.getWhatsAppCostStatement(delMes.id),
                todasLasPaginas((cursor) => ops.listWhatsAppCostDiscrepancies({ statement_id: delMes.id, cursor, limit: 100 })),
                todasLasPaginas((cursor) => ops.getWhatsAppUsageSummary({ period: periodo, cursor, limit: 100 })),
            ]);
            return { extracto, diferencias, consumo };
        };
        cargar()
            .then((datos) => {
                if (vivo) {
                    setMesCargado({ periodo, datos });
                }
            })
            .catch((e: unknown) => {
                if (vivo && !errorDelMarco(e, { onSesionCaducada, onFaltanDosPasos })) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, extractos, periodo, onSesionCaducada, onFaltanDosPasos]);

    if (error !== null) {
        return estadoDeError(error).tipo === 'sin-permiso' ? (
            <SinPermiso seccion="Conciliación YCloud" superadmin={p.superadmin} movil={movil} onIr={null} />
        ) : (
            <EstadoError
                seccion="Conciliación YCloud"
                codigoSoporte={estadoDeError(error).codigoSoporte}
                movil={movil}
                onReintentar={() => {
                    setError(null);
                    setExtractos(null);
                    setMesCargado(null);
                    setVuelta((v) => v + 1);
                }}
            />
        );
    }

    const datos = mesCargado?.periodo === periodo ? mesCargado.datos : null;
    const moneda = simboloMoneda(datos?.extracto?.total_excl_tax.currency ?? 'EUR');
    const filas = datos?.extracto == null ? [] : filasConciliacion(datos.extracto, datos.diferencias, datos.consumo);
    const extracto = datos?.extracto ?? null;
    const cierre = extracto === null ? null : estadoCierre(extracto);
    const cifra = (m: { amount: number; currency: string } | null | undefined) => (m == null ? '—' : cifraDinero(m));

    // «Cerrar el mes» (G1152): con diferencias abiertas, la API responde 409 y se dice por qué.
    const cerrarMes = () => {
        if (extracto === null || cerrando) {
            return;
        }
        setCerrando(true);
        ops.closeWhatsAppCostStatement(extracto.id)
            .then((cerrado) => {
                setMesCargado((m) => (m === null ? m : { ...m, datos: { ...m.datos, extracto: cerrado } }));
            })
            .catch((e: unknown) => {
                if (!errorDelMarco(e, { onSesionCaducada, onFaltanDosPasos })) {
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No se ha podido cerrar el mes. Prueba de nuevo.') });
                }
            })
            .finally(() => {
                setCerrando(false);
            });
    };

    return (
        <div className={`rv-boy${movil ? ' rv-boy--movil' : ''}`}>
            <div className="rv-boy-periodo">
                <div className="rv-boy-ano">
                    <button
                        type="button"
                        aria-label="Año anterior"
                        onClick={() => {
                            setMes((x) => ({ y: x.y - 1, m: x.m }));
                        }}
                    >
                        <Icono nombre="chevron-left" />
                    </button>
                    <span>{String(mes.y)}</span>
                    <button
                        type="button"
                        aria-label="Año siguiente"
                        disabled={mes.y >= hoy.y}
                        onClick={() => {
                            setMes((x) => ({ y: x.y + 1, m: x.y + 1 === hoy.y ? Math.min(x.m, hoy.m) : x.m }));
                        }}
                    >
                        <Icono nombre="chevron-right" />
                    </button>
                </div>
                <div className="rv-boy-tira">
                    <div className="rv-boy-meses" role="group" aria-label="Mes">
                        <span className="rv-boy-meses__pildora" style={{ transform: `translateX(${String(mes.m * 100)}%)` }} />
                        {MESES_CORTOS.map((etiqueta, i) => (
                            <button
                                key={etiqueta}
                                type="button"
                                aria-pressed={i === mes.m}
                                disabled={mesFuturo({ y: mes.y, m: i }, ahora)}
                                onClick={() => {
                                    setMes((x) => ({ y: x.y, m: i }));
                                }}
                            >
                                {etiqueta}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="rv-boy-acciones">
                <Boton
                    tipo="secundario"
                    icono="plus"
                    movil={movil}
                    onClick={() => {
                        setPanelCoste(true);
                    }}
                >
                    Registrar el coste del trimestre
                </Boton>
                {/* Provisional hasta C.68: «Importar el extracto de YCloud» abre la subida del CSV. */}
                <Boton
                    tipo="secundario"
                    icono="upload"
                    movil={movil}
                    onClick={() => {
                        setPanelImportar(true);
                    }}
                >
                    Importar el extracto de YCloud
                </Boton>
            </div>

            {datos === null ? (
                <EstadoCargando seccion="Conciliación YCloud" />
            ) : extracto === null ? (
                <div className="rv-bo-vacia">No hay extracto de YCloud de {textoDelMes(mes)}.</div>
            ) : (
                <>
                    <div className="rv-boy-cifras">
                        <Cifra moneda={moneda} titulo="YCloud nos cobra" cifra={cifraDinero(extracto.total_excl_tax)} pie="Según su extracto" />
                        <Cifra moneda={simboloMoneda(extracto.recorded_total?.currency ?? 'EUR')} titulo="Nosotros contamos" cifra={cifra(extracto.recorded_total)} pie="Mensajes enviados" />
                        <Cifra
                            moneda={simboloMoneda(extracto.difference_total?.currency ?? 'EUR')}
                            titulo="Diferencia"
                            cifra={cifra(extracto.difference_total)}
                            chip={extracto.open_discrepancies > 0 ? `${String(extracto.open_discrepancies)} por resolver` : null}
                            pie={extracto.open_discrepancies > 0 ? 'En líneas sin resolver' : 'Cuadra con el extracto'}
                        />
                        <Cifra moneda={simboloMoneda(extracto.wallet_balance?.currency ?? MONEDA_MONEDERO)} titulo="Monedero" cifra={cifra(extracto.wallet_balance)} pie="Saldo al cierre" />
                        <Cifra moneda={moneda} titulo="Margen" cifra={cifra(extracto.margin)} pie="Venta de paquetes menos coste de Meta, sin IVA" />
                    </div>
                    {/* Provisional hasta C.67: «Cerrar el mes», el aviso de por qué no se puede y el estado «Cerrado» con la fecha. */}
                    {cierre !== null && (
                        <div className="rv-boy-cierre" role="region" aria-label="Cierre del mes">
                            <span className="rv-boy-cierre__texto">
                                {cierre.cerrado && <Chip estado="confirmed">Cerrado</Chip>}
                                <span>{cierre.texto}</span>
                            </span>
                            {!cierre.cerrado && (
                                <Boton tipo="secundario" icono="lock" movil={movil} disabled={!cierre.puede} ocupado={cerrando} ocupadoTexto="Cerrando…" onClick={cerrarMes}>
                                    Cerrar el mes
                                </Boton>
                            )}
                        </div>
                    )}
                    <div className="rv-boy-caja" role="table" aria-label={`Conciliación de ${textoDelMes(mes)}`}>
                        {!movil && (
                            <div className="rv-boy-cabtabla" role="row">
                                <span role="columnheader">Cuenta y categoría</span>
                                <span role="columnheader">YCloud</span>
                                <span role="columnheader">Nosotros</span>
                                <span role="columnheader">Diferencia</span>
                            </div>
                        )}
                        {filas.map((f) => (
                            <div key={f.clave} className="rv-boy-fila" role="row" aria-label={`${f.negocio} · ${f.categoria}`}>
                                <div className="rv-boy-fila__cifras">
                                    <span className="rv-boy-fila__nombre" role="cell">
                                        <strong>{f.negocio}</strong>
                                        <span>{f.categoria}</span>
                                    </span>
                                    <span className="rv-boy-num" role="cell">
                                        {f.ycloud}
                                    </span>
                                    <span className="rv-boy-num" role="cell">
                                        {f.nosotros}
                                    </span>
                                    <span className={`rv-boy-dif${f.abierta ? ' rv-boy-dif--abierta' : ''}`} role="cell">
                                        {f.bien && <Icono nombre="check" />}
                                        {f.diferencia}
                                    </span>
                                </div>
                                {f.abierta && (
                                    <div className="rv-boy-fila__acciones">
                                        {resolucionesDe(f).map((r) => (
                                            <Boton
                                                key={r}
                                                tipo="secundario"
                                                icono={RESOLUCIONES[r].icono}
                                                movil={movil}
                                                onClick={() => {
                                                    setResolver({ fila: f, resolucion: r });
                                                }}
                                            >
                                                {RESOLUCIONES[r].boton}
                                            </Boton>
                                        ))}
                                    </div>
                                )}
                                {f.hecha !== null && <span className="rv-boy-fila__hecha">{f.hecha}</span>}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <div className="rv-boy-costes">
                <div className="rv-boy-costes__cab">
                    <strong>Coste por mensaje de Meta</strong>
                    <span>España, sin IVA. Sirve para el margen y la conciliación.</span>
                </div>
                {costes !== null && (
                    <div className="rv-boy-caja">
                        {filasCoste(costes, ahora).map((c) => (
                            <div key={c.categoria} className="rv-boy-coste" role="group" aria-label={c.categoria}>
                                <span className="rv-boy-coste__textos">
                                    <strong>{c.categoria}</strong>
                                    <span>{c.desde}</span>
                                </span>
                                {c.programado && <Chip estado="pending">Programado</Chip>}
                                <span className="rv-boy-coste__cifra">{c.coste}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Provisional hasta C.69: «Recargas del monedero», con la lista y el alta en una ventana. */}
            <div className="rv-boy-costes" role="region" aria-label="Recargas del monedero">
                <div className="rv-boy-costes__cab rv-boy-recargas__cab">
                    <span>
                        <strong>Recargas del monedero</strong>
                        <span>El saldo de prepago de Reservaria en YCloud, en dólares. Sin saldo, ningún negocio puede enviar WhatsApp.</span>
                    </span>
                    <Boton
                        tipo="secundario"
                        icono="plus"
                        movil={movil}
                        onClick={() => {
                            setVentanaRecarga(true);
                        }}
                    >
                        Registrar una recarga
                    </Boton>
                </div>
                {recargas !== null && (
                    <div className="rv-boy-caja">
                        {recargas.length === 0 && <div className="rv-boy-coste">Aún no hay recargas registradas.</div>}
                        {recargas.map((r) => {
                            const f = filaRecarga(r);
                            return (
                                <div key={r.id} className="rv-boy-coste" role="group" aria-label={`Recarga del ${f.fecha}`}>
                                    <span className="rv-boy-coste__textos">
                                        <strong>{f.fecha}</strong>
                                        <span>{f.referencia}</span>
                                    </span>
                                    <span className="rv-boy-coste__cifra">{f.importe}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {ventanaRecarga && (
                <VentanaRecarga
                    ops={ops}
                    movil={movil}
                    onCerrar={() => {
                        setVentanaRecarga(false);
                    }}
                    onHecho={() => {
                        setVentanaRecarga(false);
                        setVueltaRecargas((v) => v + 1);
                    }}
                />
            )}
            <PanelImportar
                abierto={panelImportar}
                ops={ops}
                movil={movil}
                mes={mes}
                ahora={ahora}
                onCerrar={() => {
                    setPanelImportar(false);
                }}
                onHecho={(importado, delMes) => {
                    setPanelImportar(false);
                    setMes(delMes);
                    setMesCargado(null);
                    setExtractos((l) => (l === null ? [importado] : [importado, ...l.filter((x) => x.id !== importado.id)]));
                }}
            />
            {resolver !== null && resolver.fila.discrepancia !== null && (
                <VentanaResolver
                    ops={ops}
                    movil={movil}
                    fila={resolver.fila}
                    discrepancia={resolver.fila.discrepancia}
                    resolucion={resolver.resolucion}
                    onCerrar={() => {
                        setResolver(null);
                    }}
                    onHecho={(nueva) => {
                        setMesCargado((m) =>
                            m === null
                                ? m
                                : {
                                      ...m,
                                      datos: {
                                          ...m.datos,
                                          diferencias: m.datos.diferencias.map((d) => (d.id === nueva.id ? nueva : d)),
                                          // Una reclamada (`claimed`) sigue abierta (G1153): solo resta la que se resuelve.
                                          extracto: m.datos.extracto === null ? null : { ...m.datos.extracto, open_discrepancies: Math.max(0, m.datos.extracto.open_discrepancies - (nueva.status === 'resolved' ? 1 : 0)) },
                                      },
                                  },
                        );
                        setResolver(null);
                    }}
                />
            )}
            <PanelCoste
                abierto={panelCoste}
                ops={ops}
                movil={movil}
                ahora={ahora}
                onCerrar={() => {
                    setPanelCoste(false);
                }}
                onHecho={() => {
                    setPanelCoste(false);
                    setVueltaCostes((v) => v + 1);
                }}
            />
        </div>
    );
}

/** Tarjeta de cifra (RV Tarjeta de cifra) con el chip junto a la cifra, como la «Diferencia» del prototipo. */
function Cifra({ titulo, cifra, moneda, pie, chip = null }: { titulo: string; cifra: string; moneda: string; pie: string; chip?: string | null }) {
    return (
        <section className="rv-tcifra" aria-label={titulo}>
            <div className="rv-tcifra__cab">
                <strong>{titulo}</strong>
            </div>
            <div className="rv-tcifra__cifra">
                <span>{cifra}</span>
                {cifra !== '—' && <small>{moneda}</small>}
                {chip !== null && <b className="rv-boy-chip">{chip}</b>}
            </div>
            <div className="rv-tcifra__pie">{pie}</div>
        </section>
    );
}
